import React, { useContext, useState } from 'react';
import {
  Button, ButtonGroup, Drawer, IconButton, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Close } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import { FilterContext } from 'shared/hoc/withFilters';
import SplitButton from 'shared/components/SplitButton';
import { useNotification } from 'shared/hooks';
import { FILTER_DISPLAY_TYPE, VARIANTS } from 'shared/constants';
import CollapsibleSection from '../CollapsibleSection';

// Applied filters inside a group, as an accent badge at the end of the group's header.
function AppliedCount({ count }) {
  if (!count) return null;
  return (
    <Box
      component="span"
      aria-label={`${count} applied`}
      sx={{
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: '10px',
        display: 'inline-grid',
        placeItems: 'center',
        fontSize: '0.75rem',
        fontWeight: 700,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
      }}
    >
      {count}
    </Box>
  );
}

/**
 * @param {Object} props
 * @param {Object<string, *>} props.filterState
 * @param {Function} props.setFilters
 * @param {string} props.tableName
 * @param {Array<Object>} props.filters
 * @param {Function} props.filters[].FilterComponent
 * @param {string} [props.filters[].name]
 * @param {string} [props.filters[].param]
 * @param {string} [props.filterKey='']
 * @param {string} props.search
 */
// Renders a button that opens an interface for filtering tables
export default function FilterModal(props) {
  const {
    filterState,
    setFilters,
    tableName,
    filters,
    filterKey = '',
    search,
  } = props;

  const [show, setShow] = useState(false);
  const [dirtyFilterState, setDirtyFilterState] = useState({});
  const { setFavoriteFilters } = useContext(FilterContext);
  const notification = useNotification();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setShow(open);
  };

  // Sets the state without triggering reload.
  // When the modal is closed they are added.
  const stageFilters = (key, value) => {
    setDirtyFilterState({
      ...dirtyFilterState,
      [key]: value,
    });
  };

  if (!filters || filters.length === 0) return null;
  const numFilters = Object.values(filterState).filter((x) => x).length;
  const getAppliedCount = (items) => {
    const keys = items?.map((filter) => filter.param);
    return Object.keys(filterState).filter((key) => keys?.includes(key) && filterState[key]).length;
  };

  // Compares current url values to the current state.
  // It creates a list of filter tuples that need to be updated.
  const onApply = () => {
    const searchParams = new URLSearchParams(search);

    const newFilters = [];
    Object.keys(dirtyFilterState).forEach((key) => {
      // .get returns null when the value doesn't exist. Change to an empty string.
      const currURLValue = searchParams.get(key) ? searchParams.get(key) : '';
      const currFilterValue = dirtyFilterState[key];
      if (currURLValue !== currFilterValue) {
        newFilters.push([key, currFilterValue]);
      }
    });

    if (newFilters.length > 0) {
      setFilters([['p', ''], ...newFilters]);
    }
    notification.pushInfo('Filters Applied');
    setDirtyFilterState({});
    setShow(false);
  };

  const showDialog = () => {
    setDirtyFilterState(filterState);
    setShow(true);
  };

  const hideDialog = () => {
    setDirtyFilterState({});
    setShow(false);
  };

  function filterStateToSearchParams(filterStateObject) {
    const searchParams = new URLSearchParams(search);
    const filterObj = {};
    Object.entries(filterStateObject).forEach((pair) => {
      const key = pair[0];
      const value = pair[1];

      // Update the URL to reflect the new filter.
      if (value) {
        if (Array.isArray(value)) {
          searchParams.delete(key);
          value.forEach((val) => {
            // only append to the search params if the key value pair does not already exist
            searchParams.append(key, val);
          });
        } else {
          searchParams.set(key, value);
        }
      } else searchParams.delete(key);
    });

    filterObj[filterKey] = searchParams.toString();
    return filterObj;
  }

  const setFavoriteFiltersAndApply = () => {
    setFavoriteFilters(filterStateToSearchParams(dirtyFilterState));
    onApply();
  };

  const createControl = (filter) => {
    const {
      FilterComponent,
      label,
      param,
      baseSearchParam,
    } = filter;
    // This stops react from erroring about these being passed as props
    const filterProps = { dirtyFilterState, baseSearchParam };

    return (
      <Grid key={label} size={12}>
        <FilterComponent
          setValue={(newValue) => stageFilters(param, newValue)}
          value={dirtyFilterState[param] ? dirtyFilterState[param] : undefined}
          label={label}
          {...filterProps}
        />
      </Grid>
    );
  };

  // Opens by itself when a filter inside is applied, so that state isn't hidden.
  const createGroup = (group) => {
    const applied = getAppliedCount(group.items);
    return (
      <Grid key={group.key} size={12}>
        <CollapsibleSection
          title={group.label}
          headerEnd={<AppliedCount count={applied} />}
          defaultOpen={applied > 0}
        >
          <Grid container rowSpacing={2}>
            {group.items.map((subItem) => createControl(subItem))}
          </Grid>
        </CollapsibleSection>
      </Grid>
    );
  };

  const drawerContent = (
    <Box
      sx={{ width: 300, margin: '0px 10px' }}
      role="presentation"
    >
      <IconButton
        edge="start"
        sx={{
          marginLeft: '5px',
          alignSelf: 'start',
          float: 'right',
        }}
        onClick={hideDialog}
        aria-label="close"
      >
        <Close />
      </IconButton>
      <br />
      <Typography sx={{ flex: 1, marginBottom: '25px' }} variant="h5" component="div">
        {`Filter ${tableName}`}
      </Typography>
      <Grid container rowSpacing={2}>
        {filters.map((item) => (item.type === FILTER_DISPLAY_TYPE.control && (
          createControl(item)
        )) || (item.type === FILTER_DISPLAY_TYPE.group && (
          createGroup(item)
        )))}
      </Grid>
      <ButtonGroup sx={{ float: 'right' }}>
        <SplitButton
          disabled={Object.values(dirtyFilterState).length === 0}
          title="Apply"
          onClick={onApply}
          menuItems={[{
            menuItemClick: setFavoriteFiltersAndApply,
            menuItemTitle: 'Apply & Save Favorite',
          }]}
        />
      </ButtonGroup>
    </Box>
  );

  return (
    <>
      <Badge
        badgeContent={numFilters}
        invisible={!numFilters}
        overlap="rectangular"
        sx={{
          '& .MuiBadge-badge': {
            right: 1,
            top: 1,
            // Sits on the primary Button, so it inverts against it (CHIP in tokens.js).
            backgroundColor: 'status.onAccentChip',
            color: 'status.onAccentChipInk',
          },
        }}
      >
        <Button
          color={VARIANTS.primary}
          variant={VARIANTS.contained}
          onClick={showDialog}
        >
          Filter
        </Button>
      </Badge>
      <Drawer
        anchor="left"
        open={show}
        onClose={toggleDrawer(false)}
        sx={{ minWidth: 'fit-content', zIndex: (theme) => theme.zIndex.swipeDrawer }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
